import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import Pagination from '../../../components/Pagination';
import client from '../../../lib/feathers';

const STATUS_LIST = ['active', 'paused', 'archived'];

const AUDIENCE_LIST = [
  { value: 'outreach', label: 'Outreach' },
  { value: 'business', label: 'Business' },
  { value: 'personal', label: 'Personal' },
];

const statusBadgeStyles = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  archived: 'bg-gray-100 text-gray-800',
};

const audienceBadgeStyles = {
  outreach: 'bg-blue-100 text-blue-800',
  business: 'bg-purple-100 text-purple-800',
  personal: 'bg-emerald-100 text-emerald-800',
};

const getAudienceLabel = (value) => {
  const match = AUDIENCE_LIST.find((a) => a.value === value);
  return match ? match.label : value;
};

export default function OutreachJourneys() {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const limit = 25;

  const [page, setPage] = useState(Number(query.get('page')) || 0);
  const [statusFilter, setStatusFilter] = useState(query.get('status') || undefined);
  const [audienceFilter, setAudienceFilter] = useState(query.get('audience') || '');

  const [journeys, setJourneys] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const getJourneys = async () => {
    setLoading(true);
    setError(null);
    try {
      const q = {
        $limit: limit,
        $skip: page * limit,
        $sort: { createdAt: -1 },
      };

      if (statusFilter) {
        q.status = statusFilter;
      }

      if (audienceFilter) {
        q.audience = audienceFilter;
      }

      const result = await client.service('admin/outreach-journey').find({ query: q });
      setJourneys(result.data);
      setTotal(result.total);
    } catch (e) {
      console.error('Error loading journeys:', e);
      setError('There was a problem loading journeys. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const navigateToPage = (p = 0) => {
    if (p * limit > total || p * limit < 0) return;
    navigate(`/admin/outreach/journeys?page=${p}`);
    setPage(p);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status || undefined);
    setPage(0);
  };

  const handleAudienceFilter = (e) => {
    setAudienceFilter(e.target.value);
    setPage(0);
  };

  const handleToggleStatus = async (e, journey) => {
    e.stopPropagation();
    if (journey.status === 'archived') return;
    const nextStatus = journey.status === 'active' ? 'paused' : 'active';
    setTogglingId(journey.id);
    try {
      await client.service('admin/outreach-journey').patch(journey.id, { status: nextStatus });
      await getJourneys();
    } catch (err) {
      console.error('Error toggling journey status:', err);
      setError('Failed to update journey status. Please try again.');
    } finally {
      setTogglingId(null);
    }
  };

  useEffect(() => {
    getJourneys();
  }, [page, statusFilter, audienceFilter]);

  return (
    <Page title="Outreach Journeys">

      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb
            elements={[
              { name: 'Outreach', href: '/admin/outreach' },
              { name: 'Journeys' },
            ]}
          />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/admin/outreach/journeys/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700"
          >
            New Journey
          </Link>
        </div>
      </div>

      <div className="pb-4 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Journeys ({total} Total)
        </h3>
        <div className="mt-3 sm:mt-0">
          <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
        </div>
      </div>

      <div className="py-3 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <div className="sm:hidden">
            <label htmlFor="status-select" className="sr-only">Filter by status</label>
            <select
              id="status-select"
              value={statusFilter || ''}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm rounded-md"
            >
              <option value="">All Statuses</option>
              {STATUS_LIST.map((s) => (
                <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="hidden sm:block">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-6" aria-label="Status filter">
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleStatusFilter(undefined); }}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${!statusFilter ? 'border-gray-500 text-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                >
                  All
                </a>
                {STATUS_LIST.map((s) => (
                  <a
                    key={s}
                    href="#"
                    onClick={(e) => { e.preventDefault(); handleStatusFilter(s); }}
                    className={`capitalize whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${statusFilter === s ? 'border-gray-500 text-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                  >
                    {s}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="sm:w-48">
          <label htmlFor="audience-select" className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Audience
          </label>
          <select
            id="audience-select"
            value={audienceFilter}
            onChange={handleAudienceFilter}
            className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-gray-500 focus:border-gray-500 rounded-md"
          >
            <option value="">All Audiences</option>
            {AUDIENCE_LIST.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Audience
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Steps
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading && (
                    <tr>
                      <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                        Loading journeys...
                      </td>
                    </tr>
                  )}
                  {!loading && journeys.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                        No journeys found.
                      </td>
                    </tr>
                  )}
                  {!loading && journeys.map((j) => {
                    const isToggling = togglingId === j.id;
                    const canToggle = j.status !== 'archived';
                    const completedCount = (j.enrollmentCount || 0) - (j.activeEnrollmentCount || 0);

                    return (
                      <tr
                        key={j.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/admin/outreach/journeys/${j.id}`)}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{j.name}</div>
                          {j.description && (
                            <div className="text-xs text-gray-400 mt-0.5 max-w-xs truncate">{j.description}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${audienceBadgeStyles[j.audience] || 'bg-gray-100 text-gray-800'}`}>
                            {getAudienceLabel(j.audience)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${statusBadgeStyles[j.status] || 'bg-gray-100 text-gray-800'}`}>
                            {j.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {j.stepCount != null ? j.stepCount : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {j.activeEnrollmentCount != null ? j.activeEnrollmentCount.toLocaleString() : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {j.enrollmentCount != null ? completedCount.toLocaleString() : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(j.createdAt), 'MMM d, yyyy')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-3">
                            {canToggle && (
                              <button
                                type="button"
                                disabled={isToggling}
                                onClick={(e) => handleToggleStatus(e, j)}
                                className={`inline-flex items-center px-2.5 py-1 border rounded text-xs font-medium transition-colors
                                  ${j.status === 'active'
                                    ? 'border-yellow-300 text-yellow-700 bg-yellow-50 hover:bg-yellow-100'
                                    : 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'}
                                  ${isToggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                {isToggling
                                  ? '...'
                                  : j.status === 'active' ? 'Pause' : 'Resume'}
                              </button>
                            )}
                            <Link
                              to={`/admin/outreach/journeys/${j.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary hover:text-red-900"
                            >
                              View
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
            </div>
          </div>
        </div>
      </div>

    </Page>
  );
}
